import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { getArticleBySlug } from '../services/blog';
import './BlogPage.css';

function formatDate(d) {
  if (!d) return '';
  return new Intl.DateTimeFormat('fr-FR', { dateStyle: 'long' }).format(d);
}

function readingMinutes(html) {
  if (!html) return 1;
  const words = html.replace(/<[^>]+>/g, ' ').trim().split(/\s+/).length;
  return Math.max(1, Math.round(words / 220));
}

function BlogArticlePage() {
  const { slug } = useParams();
  const [article, setArticle] = useState(null);
  const [state, setState] = useState('loading');

  useEffect(() => {
    window.scrollTo(0, 0);
    let cancelled = false;
    setState('loading');
    getArticleBySlug(slug)
      .then((a) => {
        if (cancelled) return;
        if (!a) {
          setState('notfound');
        } else {
          setArticle(a);
          setState('ready');
        }
      })
      .catch((err) => {
        console.error(err);
        if (cancelled) return;
        setState('error');
      });
    return () => {
      cancelled = true;
    };
  }, [slug]);

  if (state === 'loading') {
    return (
      <div className="blog-wrapper">
        <div className="blog-container blog-empty">Chargement…</div>
      </div>
    );
  }

  if (state === 'notfound') {
    return (
      <div className="blog-wrapper">
        <div className="blog-container">
          <title>Article introuvable — CrousRadar</title>
          <meta name="robots" content="noindex" />
          <h1 className="blog-article-title">Article introuvable</h1>
          <p>
            Cet article n'existe pas ou n'est plus publié.{' '}
            <Link to="/blog">Retour à l'actu</Link>.
          </p>
        </div>
      </div>
    );
  }

  if (state === 'error' || !article) {
    return (
      <div className="blog-wrapper">
        <div className="blog-container blog-empty">Erreur de chargement.</div>
      </div>
    );
  }

  const canonical = `https://crousradar.fr/blog/${article.slug}`;
  const cover = article.coverImage
    ? (article.coverImage.startsWith('http') || article.coverImage.startsWith('data:')
        ? article.coverImage
        : `https://crousradar.fr${article.coverImage}`)
    : 'https://crousradar.fr/og-image.jpg';

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.seoDescription || article.excerpt,
    image: cover,
    author: { '@type': 'Organization', name: article.author || 'CrousRadar' },
    publisher: {
      '@type': 'Organization',
      name: 'CrousRadar',
      logo: {
        '@type': 'ImageObject',
        url: 'https://crousradar.fr/favicon.ico'
      }
    },
    datePublished: article.publishedAt ? article.publishedAt.toISOString() : undefined,
    dateModified: article.updatedAt
      ? article.updatedAt.toISOString()
      : article.publishedAt
      ? article.publishedAt.toISOString()
      : undefined,
    mainEntityOfPage: { '@type': 'WebPage', '@id': canonical }
  };

  const minutes = readingMinutes(article.content);

  return (
    <article className="blog-article">
      {/* React 19 hoists these to <head> */}
      <title>{article.seoTitle || article.title}</title>
      <meta name="description" content={article.seoDescription || article.excerpt} />
      <link rel="canonical" href={canonical} />
      <meta property="og:type" content="article" />
      <meta property="og:title" content={article.seoTitle || article.title} />
      <meta property="og:description" content={article.seoDescription || article.excerpt} />
      <meta property="og:url" content={canonical} />
      <meta property="og:image" content={cover} />
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={article.seoTitle || article.title} />
      <meta name="twitter:description" content={article.seoDescription || article.excerpt} />
      <meta name="twitter:image" content={cover} />
      <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>

      <Link to="/blog" className="blog-back">← Retour à l'actu</Link>

      <header className="blog-article-header">
        {article.category && (
          <span className="blog-article-pill">{article.category}</span>
        )}
        <h1 className="blog-article-title">{article.title}</h1>
        <div className="blog-article-meta">
          <span>{article.author}</span>
          <span>·</span>
          <span>{formatDate(article.publishedAt)}</span>
          <span>·</span>
          <span>{minutes} min de lecture</span>
        </div>
      </header>

      {article.coverImage && (
        <div className="blog-article-cover-wrap">
          <img
            src={article.coverImage}
            alt={article.title}
            className="blog-article-cover"
          />
        </div>
      )}

      <div
        className="blog-article-body"
        dangerouslySetInnerHTML={{ __html: article.content }}
      />

      {article.tags && article.tags.length > 0 && (
        <div className="blog-tags">
          {article.tags.map((t) => (
            <span key={t} className="blog-tag">#{t}</span>
          ))}
        </div>
      )}
    </article>
  );
}

export default BlogArticlePage;
